import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import { returnsToCentered } from '../utils/returns';

/**
 * ReturnsLineChart - Line chart showing returns over time
 * 
 * @param {Object} props
 * @param {Array<number>} props.returnsByEvent - Array of return values per event
 * @param {number} props.lastEventReturn - Most recent return value (for highlighting)
 * @param {string} props.trainingDataCutoffDate - Date string for cutoff (events before this are training)
 * @param {Array<string>} props.eventDates - Array of event dates corresponding to returnsByEvent
 */
export default function ReturnsLineChart({ returnsByEvent = [], lastEventReturn, trainingDataCutoffDate, eventDates }) {
  if (!returnsByEvent || returnsByEvent.length === 0) {
    return (
      <div className="chart-empty">
        No returns data available
      </div>
    );
  }

  // Helper function to parse date string without timezone issues
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Transform data for chart: centered at 0 (raw multiplier -> (raw - 1) * 100)
  const data = returnsByEvent.map((returnValue, index) => {
    const eventDate = eventDates?.[index] || null;
    const isTraining = eventDate && trainingDataCutoffDate 
      ? parseDate(eventDate) < parseDate(trainingDataCutoffDate)  // Strictly less than (BEFORE cutoff date)
      : false;
    
    const centered = returnsToCentered(returnValue);
    const returnPercent = centered !== null ? parseFloat(centered.toFixed(2)) : 0;
    
    return {
      event: index + 1,
      return: returnPercent,
      rawReturn: returnValue,
      date: eventDate,
      isTraining: isTraining,
      trainingReturn: isTraining ? returnPercent : null,
      actualReturn: !isTraining ? returnPercent : null
    };
  });

  // Determine if we should use dates on X-axis (if we have dates and cutoff date)
  const useDates = eventDates && eventDates.length > 0 && trainingDataCutoffDate;
  const hasTrainingData = trainingDataCutoffDate && data.some(d => d.isTraining);
  const hasActualData = trainingDataCutoffDate && data.some(d => !d.isTraining);
  
  // Find the cutoff event index for the vertical separator
  const cutoffEventIndex = useDates ? data.findIndex(d => d.date && parseDate(d.date) >= parseDate(trainingDataCutoffDate)) : -1;

  // Custom label component for training cutoff
  const TrainingCutoffLabel = ({ viewBox }) => {
    if (!viewBox) return null;
    // Position label above the chart area
    const labelY = Math.max(5, viewBox.y - 5);
    return (
      <g>
        <text
          x={viewBox.x}
          y={labelY}
          fill="#ECE9F3"
          fontSize={14}
          fontWeight={700}
          textAnchor="middle"
          style={{ 
            pointerEvents: 'none'
          }}
        >
          Training cutoff
        </text>
      </g>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = parseDate(dateString);
        if (!date) return null;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      };
      
      return (
        <div style={{
          backgroundColor: '#15121D',
          padding: '8px 12px',
          borderRadius: '8px',
          border: '1px solid #3A3349'
        }}>
          <p style={{ margin: 0, color: '#ECE9F3', fontSize: '12px' }}>
            {data.date ? formatDate(data.date) : `Event ${data.event}`}
          </p>
          <p style={{ margin: '4px 0 0 0', color: (data.return ?? data.trainingReturn ?? data.actualReturn ?? 0) >= 0 ? '#3DD68C' : '#FF6B6B', fontSize: '11px' }}>
            Return: {(data.return ?? data.trainingReturn ?? data.actualReturn ?? 0) >= 0 ? '+' : ''}{(data.return ?? data.trainingReturn ?? data.actualReturn ?? 0).toFixed(2)}%
          </p>
          {data.isTraining !== undefined && (
            <p style={{ margin: '4px 0 0 0', color: data.isTraining ? '#6E6780' : '#9B72E3', fontSize: '10px' }}>
              {data.isTraining ? 'Training data' : 'Actual performance'}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Calculate Y-axis domain (centered at 0, allow negative)
  const allReturns = data.map(d => d.return);
  const padding = 5;
  const minReturn = Math.min(...allReturns, 0) - padding;
  const maxReturn = Math.max(...allReturns, 0) + padding;

  return (
    <div className="chart-block">
      <h3>
        Returns over time
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 40, right: 30, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2A2536" />
          <XAxis 
            dataKey={useDates ? "date" : "event"}
            stroke="#6E6780"
            tick={{ fill: '#9A93AB', fontSize: 12 }}
            tickFormatter={useDates ? (value) => {
              if (!value) return '';
              const date = parseDate(value);
              if (!date) return '';
              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            } : undefined}
            label={{ 
              value: useDates ? 'Date' : 'Event number', 
              position: 'insideBottom', 
              offset: -5,
              style: { fill: '#9A93AB', fontSize: 12 }
            }}
          />
          <YAxis 
            stroke="#6E6780"
            tick={{ fill: '#9A93AB', fontSize: 12 }}
            domain={[minReturn, maxReturn]}
            label={{ 
              value: 'Return (%)', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: '#9A93AB', fontSize: 12 }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="line"
          />
          <ReferenceLine y={0} stroke="#6E6780" strokeWidth={2} strokeDasharray="4 4" />
          
          {/* Training data line (if we have training data and cutoff date) */}
          {hasTrainingData && (
            <Line 
              type="monotone" 
              dataKey="trainingReturn" 
              stroke="#6E6780"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 3, fill: '#6E6780' }}
              activeDot={{ r: 5, fill: '#6E6780' }}
              name="Training data"
              connectNulls={false}
            />
          )}
          
          {/* Actual data line */}
          {hasActualData ? (
            <Line 
              type="monotone" 
              dataKey="actualReturn" 
              stroke="#9B72E3" 
              strokeWidth={2}
              dot={{ r: 3, fill: '#9B72E3' }}
              activeDot={{ r: 5, fill: '#9B72E3' }}
              name="Actual performance"
              connectNulls={false}
            />
          ) : (
            // Fallback: single line if no training/actual separation
            <Line 
              type="monotone" 
              dataKey="return" 
              stroke="#9B72E3" 
              strokeWidth={2}
              dot={{ r: 3, fill: '#9B72E3' }}
              activeDot={{ r: 5, fill: '#9B72E3' }}
            />
          )}
          
          {/* Vertical separator at cutoff date */}
          {cutoffEventIndex >= 0 && trainingDataCutoffDate && (
            <ReferenceLine 
              x={useDates ? data[cutoffEventIndex]?.date : data[cutoffEventIndex]?.event}
              stroke="#ECE9F3" 
              strokeWidth={3}
              strokeDasharray="5 5"
              strokeOpacity={1}
              label={<TrainingCutoffLabel />}
            />
          )}
          
          {lastEventReturn !== undefined && lastEventReturn !== null && returnsToCentered(lastEventReturn) !== null && (
            <ReferenceLine 
              y={returnsToCentered(lastEventReturn)} 
              stroke="#FF6B6B" 
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{ 
                value: 'Last event', 
                position: 'right',
                fill: '#FF6B6B',
                fontSize: 12
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

